<script setup lang="ts">
import FlatRouterLinkButton from '@/components/atoms/FlatRouterLinkButton.vue'
import BreadcrumbList from '@/components/molecules/BreadcrumbList.vue'
import { useRoute, RouterLink } from 'vue-router'
import {
  useDeleteStepDeliveryMutation,
  useStepDeliveryIndexPageQuery,
} from './StepDeliveryIndexPage.generated'
import IconDustBox from '@/components/icons/IconDustBox.vue'
import { useAlert } from '@/libs/alertPortal'
import ButtonIconOnly from '@/components/atoms/ButtonIconOnly.vue'
import LineMessageSendingIndexPageSideMenu from '@/pages/ChatMessageSendingJobIndexPage/ChatMessageSendingJobIndexPageSideMenu.vue'
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'

const route = useRoute('StepDeliveryIndexPage')
const deleteMutation = useDeleteStepDeliveryMutation()
const pageQuery = useStepDeliveryIndexPageQuery({
  fetchPolicy: 'no-cache',
})

const alert = useAlert()

const handleClickDeleteButton = async (stepDeliveryGUID: string) => {
  if (window.confirm('本当に削除しますか？')) {
    const result = await deleteMutation.mutate({
      input: stepDeliveryGUID,
    })

    if (result?.errors) {
      console.error('Mutation failed with error', result.errors)
      return
    }

    alert.success('ステップ配信を削除しました')

    await pageQuery.refetch()
  }
}
</script>

<template>
  <CenteredPageContainer :class="$style.page" wide>
    <div :class="[$style.page__main, $style.setting]">
      <LineMessageSendingIndexPageSideMenu />
      <div :class="$style.wrapper">
        <BreadcrumbList :links="route.meta.breadcrumbs" />
        <section :class="$style.container">
          <header :class="$style.header">
            <h1>ステップ配信</h1>
            <FlatRouterLinkButton
              variant="primary"
              size="medium"
              :to="{ name: 'StepDeliveryNewPage' }"
            >
              新規作成
            </FlatRouterLinkButton>
          </header>
          <hr :class="$style.hr" />
          <div :class="$style.containerInner" v-if="pageQuery.result.value">
            <table :class="$style.table">
              <colgroup>
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th>ステップ配信名</th>
                  <th :class="$style.deleteButton"></th>
                </tr>
              </thead>
              <tbody v-if="pageQuery.result.value.stepDeliveries.length > 0">
                <tr
                  v-for="stepDelivery of pageQuery.result.value.stepDeliveries"
                  :key="stepDelivery.guid"
                >
                  <td>
                    <RouterLink
                      :to="{
                        name: 'StepDeliveryEditPage',
                        params: {
                          entranceGUID: route.params.entranceGUID,
                          stepDeliveryGUID: stepDelivery.guid,
                        },
                      }"
                      >{{ stepDelivery.name }}
                    </RouterLink>
                  </td>
                  <td>
                    <ButtonIconOnly
                      @click="handleClickDeleteButton(stepDelivery.guid)"
                    >
                      <template #icon>
                        <IconDustBox />
                      </template>
                    </ButtonIconOnly>
                  </td>
                </tr>
              </tbody>
              <tbody
                v-else-if="pageQuery.result.value?.stepDeliveries.length === 0"
              >
                <tr>
                  <td colspan="6" :class="$style.none">
                    ステップ配信がありません
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </CenteredPageContainer>
</template>

<style module lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  width: 100%;
}

.container {
  background-color: $color-surface-primary;
  padding: 16px;
  border-radius: 10px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.paginationWrapper {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.hr {
  border: none;
  border-top: 1px solid $color-border-low-emphasis;
  margin: 16px 0;
}

.containerInner {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.table {
  border-collapse: collapse;

  thead {
    background-color: $color-background-base;

    th {
      padding: 12px;
      text-align: left;
    }
  }

  tbody {
    tr {
      &:not(:first-child) {
        border-top: 1px solid $color-border-low-emphasis;
      }

      td {
        padding: 24px 12px;

        a {
          display: block;
          color: inherit;
          text-decoration: underline;
        }
      }
    }
  }
}

.deleteButton {
  width: 50px;
}

.none {
  text-align: center;
}

.page {
  padding-bottom: 80px;

  &__header {
    &:nth-child(n + 2) {
      margin-top: 40px;
    }
  }

  &__main {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.setting {
  display: grid;
  align-items: flex-start;
  grid-gap: 40px;
  grid-template-columns: 302px 1fr;
  grid-template-rows: auto;
}
</style>
